import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ClipboardCheck, Wrench, History, Users, Building2, LogOut, FileText, ChevronRight, Edit, Trash2, Package, Receipt, BarChart3, ListChecks, FileWarning, PackageX, Table2, HardDrive, Wallet, ShoppingBag, Truck, Boxes } from "lucide-react";
import { toast } from "sonner";
import InstallPWAButton from "@/components/InstallPWAButton";

import { logoColor as LOGO_URL } from "@/lib/logos";

export default function TecnicoDashboard() {
  const { user, logout, isAdmin, api } = useAuth();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState({ total: 0, borradores: 0, completados: 0, mp: 0, mc: 0 });
  const [porDespachar, setPorDespachar] = useState(0);

  const loadReports = () => {
    api.get("/api/maintenance?limit=20").then((res) => setReports(res.data)).catch(() => {});
    api.get("/api/maintenance/stats")
      .then((res) => setStats(res.data))
      .catch(() => {});
  };

  useEffect(() => {
    loadReports();
    if (isAdmin) {
      api.get("/api/tienda/admin/pedidos/badge")
        .then((r) => setPorDespachar(r.data.por_despachar || 0)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, isAdmin]);

  const deleteDraft = async (e, reportId, folio) => {
    e.preventDefault(); e.stopPropagation();
    if (!window.confirm(`¿Eliminar borrador ${folio}?`)) return;
    try {
      await api.delete(`/api/maintenance/${reportId}`);
      toast.success("Borrador eliminado");
      loadReports();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al eliminar"); }
  };

  const drafts = reports.filter((r) => r.estado === "borrador");
  const completed = reports.filter((r) => r.estado === "completado");

  const verPortalClientes = async () => {
    try {
      const r = await api.post("/api/portal/admin-acceso");
      localStorage.setItem("odf_cliente_token", r.data.token);
      // misma pestaña: window.open tras await es bloqueado por el navegador
      window.location.assign("/cliente/dashboard");
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo abrir el portal"); }
  };

  const grupos = [
    { titulo: "Operación técnica", items: [
      { icon: ClipboardCheck, label: "Nueva MP", desc: "Crear mantención preventiva", to: "/tecnico/mantencion/nueva", color: "#0FB4B7" },
      { icon: Wrench, label: "Correctiva", desc: "Crear mantención correctiva", to: "/tecnico/correctiva/nueva", color: "#384091" },
      { icon: History, label: "Reportes", desc: "Historial y descargas", to: "/tecnico/historial", color: "#0FB4B7" },
      { icon: FileWarning, label: "Informes Técnicos", desc: "Informes de eventos especiales", to: "/tecnico/informes", color: "#384091" },
      { icon: PackageX, label: "Informes de Baja", desc: "Baja de equipos", to: "/tecnico/bajas", color: "#0FB4B7" },
    ]},
    { titulo: "Mi gestión", items: [
      { icon: Wallet, label: "Rendición de Gastos", desc: "Rinde tus gastos con foto e IA", to: "/tecnico/gastos", color: "#534AB7" },
      { icon: Boxes, label: isAdmin ? "Inventario" : "Mi Inventario", desc: isAdmin ? "Stock valorizado y por técnico" : "Repuestos asignados a mí", to: "/tecnico/inventario", color: "#E0653A" },
    ]},
    ...(isAdmin ? [
      { titulo: "Ventas y tienda", items: [
        { icon: ShoppingBag, label: "Tienda · Productos", desc: "Catálogo, precios y Mercado Pago", to: "/tecnico/tienda", color: "#E0653A" },
        { icon: Truck, label: "Pedidos", desc: "Ventas por despachar", to: "/tecnico/tienda/pedidos", color: "#E0653A", badge: porDespachar },
        { icon: Building2, label: "Ver Portal Clientes", desc: "Entrar como cliente demo para probar", onClick: verPortalClientes, color: "#0D999C" },
      ]},
      { titulo: "Análisis y comercial", items: [
        { icon: BarChart3, label: "Dashboard", desc: "Indicadores y estadísticas", to: "/tecnico/dashboard-reportes", color: "#534AB7" },
        { icon: Table2, label: "Detalle de Servicios", desc: "Tabla y exportación de servicios", to: "/tecnico/detalle-servicios", color: "#0FB4B7" },
        { icon: Receipt, label: "Cotizaciones", desc: "Crear y enviar cotizaciones", to: "/tecnico/cotizaciones", color: "#0FB4B7" },
      ]},
      { titulo: "Datos maestros", items: [
        { icon: Users, label: "Técnicos", desc: "Gestión usuarios", to: "/tecnico/admin/usuarios", color: "#384091" },
        { icon: Building2, label: "Clientes", desc: "Gestión clientes", to: "/tecnico/admin/clientes", color: "#384091" },
        { icon: HardDrive, label: "Mis Equipos", desc: "Catálogo y mantenciones por equipo", to: "/tecnico/admin/equipos", color: "#0FB4B7" },
        { icon: Package, label: "Repuestos", desc: "Catálogo repuestos", to: "/tecnico/admin/repuestos", color: "#0FB4B7" },
        { icon: ListChecks, label: "Servicios", desc: "Catálogo de servicios", to: "/tecnico/admin/servicios", color: "#384091" },
      ]},
    ] : []),
  ];


  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      <header className="bg-white border-b sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Odontofix" className="h-8" />
            <span className="text-sm font-semibold text-[#384091]">Portal Técnico</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium text-gray-800">{user?.nombre}</p>
              <p className="text-xs text-gray-500">{isAdmin ? "Administrador" : "Técnico"}</p>
            </div>
            <InstallPWAButton className="hidden sm:inline-flex" />
            <Button variant="ghost" size="sm" onClick={() => { logout(); navigate("/"); }}>
              <LogOut className="h-4 w-4 mr-1" /> Salir
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Hola, {user?.nombre?.split(" ")[0]}</h1>
          <p className="text-gray-500 text-sm">¿Qué deseas hacer?</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 mb-6">
          {[
            { label: "Total", value: stats.total, color: "#384091" },
            { label: "Borradores", value: stats.borradores, color: "#D97706" },
            { label: "Completados", value: stats.completados, color: "#059669" },
            { label: "Preventivas", value: stats.mp, color: "#0FB4B7" },
            { label: "Correctivas", value: stats.mc, color: "#534AB7" },
          ].map((s) => (
            <Card key={s.label} className="border-0 shadow-sm">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold" style={{ color: s.color }}>{s.value}</p>
                <p className="text-xs text-gray-500">{s.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Módulos agrupados */}
        {grupos.map((g) => (
          <div key={g.titulo} className="mb-6">
            <div className="flex items-center gap-3 mb-3">
              <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-gray-400">{g.titulo}</h2>
              <span className="h-px flex-1 bg-gray-200" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {g.items.map((a) => (
                <Link key={a.label} to={a.to || "#"}
                  onClick={(e) => { if (a.onClick) { e.preventDefault(); a.onClick(); } }}>
                  <Card className="border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer relative">
                    {a.badge > 0 && (
                      <span className="absolute -top-2 -right-2 z-10 h-6 min-w-6 px-1.5 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center shadow">
                        {a.badge}
                      </span>
                    )}
                    <CardContent className="p-5 flex items-start gap-4">
                      <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${a.color}15` }}>
                        <a.icon className="h-5 w-5" style={{ color: a.color }} />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-800 text-sm">{a.label}</h3>
                        <p className="text-xs text-gray-500">{a.desc}</p>
                      </div>
                      <ChevronRight className="h-4 w-4 text-gray-300 mt-1" />
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        ))}

        {/* Drafts */}
        {drafts.length > 0 && (
          <div className="mb-6">
            <h2 className="font-semibold text-gray-800 mb-3">Borradores pendientes</h2>
            <div className="space-y-2">
              {drafts.map((r) => (
                <Link key={r.id} to={r.tipo === "correctiva" ? `/tecnico/correctiva/${r.id}` : `/tecnico/mantencion/${r.id}`}>
                  <Card className="border-amber-200 bg-amber-50/50 shadow-sm hover:shadow-md transition-shadow cursor-pointer">
                    <CardContent className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Edit className="h-4 w-4 text-amber-600" />
                        <div>
                          <p className="text-sm font-medium text-gray-800">
                            {r.folio} — {r.cliente}
                            {r.tipo === "correctiva" && <span className="ml-1.5 text-[10px] bg-[#384091] text-white px-1.5 py-0.5 rounded">MC</span>}
                          </p>
                          <p className="text-xs text-gray-500">{r.marca_sillon} {r.modelo}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Badge className="bg-amber-100 text-amber-700 border-0">Retomar</Badge>
                        <button onClick={(e) => deleteDraft(e, r.id, r.folio)} className="text-red-300 hover:text-red-500 p-1" title="Eliminar borrador">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Recent completed */}
        {completed.length > 0 && (
          <div>
            <h2 className="font-semibold text-gray-800 mb-3">Últimos completados</h2>
            <div className="space-y-2">
              {completed.slice(0, 5).map((r) => (
                <Card key={r.id} className="border-0 shadow-sm">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <FileText className="h-4 w-4 text-[#0FB4B7]" />
                      <div>
                        <p className="text-sm font-medium text-gray-800">{r.folio} — {r.cliente}</p>
                        <p className="text-xs text-gray-500">{r.marca_sillon} {r.modelo} · {r.fecha} · {r.tecnico_nombre}</p>
                      </div>
                    </div>
                    <Badge className="bg-emerald-50 text-emerald-700 border-0">Completado</Badge>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
